// Dedicated geo-api auth client. The portal authenticates ONLY through
// /api/auth/* endpoints exposed by geo-api. No Supabase auth in the frontend.
//
// PRODUCTION PRESERVATION:
//  - VITE_API_BASE_URL is the canonical pilot/production base (e.g.
//    https://api.giisdec.com). The frontend MUST NOT duplicate /api — this
//    client appends `/api/auth/*` itself. If an operator accidentally sets
//    VITE_API_BASE_URL=https://api.giisdec.com/api we strip the trailing
//    `/api` so no /api/api duplication occurs.
//  - Accepts BOTH `token` / `refresh_token` and `accessToken` / `refreshToken`
//    response shapes so geo-api token naming changes do not break the portal.
//  - Accepts `primaryRole`, `role`, and `roles[]` on the user payload so any
//    role taxonomy used by geo-api hydrates cleanly.
//  - Token storage keys are shared with geoApi so authenticated business
//    workflow calls inherit the bearer token automatically.

type AuthEnv = {
  VITE_API_BASE_URL?: string;
  VITE_GEO_API_BASE_URL?: string;
  VITE_AUTH_DEMO_ENABLED?: string;
  VITE_APP_ENV?: 'development' | 'staging' | 'production';
};

import { errorMessage } from './apiError';

const env: AuthEnv = ((import.meta as unknown) as { env?: AuthEnv }).env || {};

// Strip a trailing `/api` (and any trailing slashes) so callers can safely
// configure either `https://api.giisdec.com` or `https://api.giisdec.com/api`
// without the client emitting `/api/api/auth/...`.
function normalizeBase(raw: string | undefined): string {
  if (!raw) return '';
  let b = raw.trim().replace(/\/+$/, '');
  if (/\/api$/i.test(b)) b = b.replace(/\/api$/i, '');
  return b;
}

const RAW_BASE =
  env.VITE_API_BASE_URL || env.VITE_GEO_API_BASE_URL || '';
const BASE_URL = normalizeBase(RAW_BASE);

export const AUTH_APP_ENV: 'development' | 'staging' | 'production' =
  env.VITE_APP_ENV || 'production';

export const AUTH_DEMO_ENABLED =
  AUTH_APP_ENV !== 'production' || env.VITE_AUTH_DEMO_ENABLED === 'true';

// R11P GEOLOGICAL P0.5-P9A — session architecture aligned with Admin B9R2-F1/F2.
//
//   Access token  → MEMORY ONLY (bearer authorization). Never persisted to
//                   localStorage/sessionStorage, so an XSS cannot read a
//                   long-lived credential and a full reload re-derives it from
//                   the HttpOnly refresh cookie via /api/auth/refresh.
//   Refresh token → the geo-api Secure, HttpOnly, host-only cookie. JavaScript
//                   never reads, writes or stores it. The browser sends it only
//                   on credentialed /api/auth requests.
//
// Legacy keys that earlier builds wrote to localStorage are purged on load so a
// previously-stored refresh token cannot linger in a JS-readable store.
const LEGACY_TOKEN_KEY = 'giisdec.geo.jwt';
const LEGACY_REFRESH_KEY = 'giisdec.geo.refresh';

let accessToken: string | null = null;

export function getAuthToken(): string | null {
  return accessToken;
}
export function setAuthToken(token: string | null): void {
  accessToken = token || null;
}
export function clearAuthTokens(): void {
  accessToken = null;
  // Purge any refresh/access token a prior build may have persisted.
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_REFRESH_KEY);
  } catch { /* no-op */ }
}

// One-time migration on module load: never leave a legacy JS-readable token.
try {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_KEY);
} catch { /* no-op */ }

/**
 * Deprecated. The refresh token is the HttpOnly cookie and is deliberately NOT
 * readable by JavaScript, so this always returns null. Kept only so existing
 * diagnostic callers compile; it must never gate auth logic.
 */
export function getRefreshToken(): null {
  return null;
}

export interface AuthApiResult<T> {
  data: T | null;
  error: string | null;
  status: number;
}

/**
 * Normalize geo-api error envelopes into a render-safe string.
 *
 * Supported forms:
 *   { error: "message" }
 *   { message: "message" }
 *   { error: { code, message, requestId } }
 *
 * Never return a raw object to React UI state.
 */
export function normalizeApiError(body: unknown, status: number): string {
  // Delegates to the single P9A error foundation (preserves nested code/message).
  return errorMessage(body, status);
}

async function rawRequest<T>(path: string, init: RequestInit = {}, withToken = true): Promise<AuthApiResult<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (withToken) {
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  try {
    // credentials:'include' so the HttpOnly refresh cookie travels with
    // /api/auth/refresh and /api/auth/logout. The access token is still a bearer.
    const res = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const body = isJson ? await res.json().catch(() => null) : null;
    if (!res.ok) {
      return {
        data: null,
        error: normalizeApiError(body, res.status),
        status: res.status,
      };
    }
    return { data: (body as T) ?? null, error: null, status: res.status };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Network error',
      status: 0,
    };
  }
}

/* ─────────────── geo-api auth endpoints ─────────────── */

export interface AuthUser {
  id: string;
  email: string;
  role?: string | null;          // raw single-role (legacy)
  primaryRole?: string | null;   // production pilot field
  roles?: string[] | null;       // production pilot field
  full_name?: string | null;
  organization?: string | null;
  status?: string | null;
}

// Raw login response — supports both `token`/`refresh_token` and
// `accessToken`/`refreshToken` shapes coming from geo-api.
interface RawLoginResponse {
  token?: string;
  accessToken?: string;
  refresh_token?: string;
  refreshToken?: string;
  user: AuthUser;
}

interface RawRefreshResponse {
  token?: string;
  accessToken?: string;
  refresh_token?: string;
  refreshToken?: string;
}

export interface LoginResponse {
  token: string;
  refresh_token?: string;
  user: AuthUser;
}

export interface RefreshResponse {
  token: string;
  refresh_token?: string;
}

export interface DemoAccount {
  label: string;
  email: string;
  password: string;
  role: string;
}

function unwrapLogin(raw: AuthApiResult<RawLoginResponse>): AuthApiResult<LoginResponse> {
  if (!raw.data) return { data: null, error: raw.error, status: raw.status };
  const t = raw.data.token || raw.data.accessToken;
  if (!t) {
    return { data: null, error: 'Login response missing access token.', status: raw.status };
  }
  return {
    data: {
      token: t,
      refresh_token: raw.data.refresh_token || raw.data.refreshToken,
      user: raw.data.user,
    },
    error: null,
    status: raw.status,
  };
}

function unwrapRefresh(raw: AuthApiResult<RawRefreshResponse>): AuthApiResult<RefreshResponse> {
  if (!raw.data) return { data: null, error: raw.error, status: raw.status };
  const t = raw.data.token || raw.data.accessToken;
  if (!t) return { data: null, error: 'Refresh response missing access token.', status: raw.status };
  return {
    data: { token: t, refresh_token: raw.data.refresh_token || raw.data.refreshToken },
    error: null,
    status: raw.status,
  };
}

export const authApi = {
  login: async (email: string, password: string): Promise<AuthApiResult<LoginResponse>> =>
    unwrapLogin(await rawRequest<RawLoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }, false)),

  logout: () =>
    rawRequest<{ ok: true }>('/api/auth/logout', { method: 'POST' }, true),

  me: () =>
    rawRequest<{ user: AuthUser }>('/api/auth/me', { method: 'GET' }, true),

  refresh: async (): Promise<AuthApiResult<RefreshResponse>> =>
    // Browser transport: the refresh token is the HttpOnly cookie, sent by
    // credentials:'include'. No JavaScript refresh token is read or sent.
    unwrapRefresh(await rawRequest<RawRefreshResponse>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({}),
    }, true)),

  register: async (payload: {
    email: string;
    password: string;
    role: string;
    full_name?: string;
    organization?: string;
  }): Promise<AuthApiResult<LoginResponse>> =>
    unwrapLogin(await rawRequest<RawLoginResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, false)),

  registerInvestor: async (payload: {
    email: string;
    password: string;
    fullName?: string;
  }): Promise<AuthApiResult<LoginResponse>> =>
    unwrapLogin(await rawRequest<RawLoginResponse>('/api/investors/register', {
      method: 'POST',
      body: JSON.stringify({
        email: payload.email,
        password: payload.password,
        ...(payload.fullName ? { fullName: payload.fullName } : {}),
      }),
    }, false)),

  // ── Password recovery (additive — login/refresh/token handling untouched) ──
  // Neutral by design: the UI must never disclose whether an account exists.
  // Both requests are unauthenticated and resolve under the approved base as
  // /api/auth/forgot-password and /api/auth/reset-password (never /api/api).
  forgotPassword: (email: string) =>
    rawRequest<{ ok?: boolean; message?: string }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, false),

  // Sends both `token`/`resetToken` and `password`/`newPassword` field shapes
  // so geo-api naming conventions are honoured without frontend changes.
  // The token is used transiently only — never logged, never persisted.
  resetPassword: (token: string, password: string) =>
    rawRequest<{ ok?: boolean; message?: string }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, resetToken: token, password, newPassword: password }),
    }, false),


  // Demo pilot account discovery — only used in non-production builds or when
  // VITE_AUTH_DEMO_ENABLED=true. Server may return 404 in production.
  listDemoAccounts: () =>
    rawRequest<{ accounts: DemoAccount[] }>('/api/auth/demo-accounts', { method: 'GET' }, false),

  baseUrl: BASE_URL,
};
